<template>
    <section class="org-area account-section">
        <div class="account-container-small">

            <div class="card billing-card mb-4">
                <div class="card-body card-body-padding" v-if="loaded">
                    <template v-if="organization.plan_name == 'solo' && organization.subscription_status == 'onetime'">
                        <h2 class="mb-4 mt-n2">Individual Plan</h2>
                        <h3 class="card-title pricing-card-title">$19 <small class="text-muted">/ signature</small></h3>
                        <ul class="pricing-list list-unstyled my-3">
                            <li>Premium Templates</li>
                            <li>Premium Fields & Features</li>
                            <li>Premium Icons</li>
                            <li>Custom Icon Colors</li>
                            <li>Signature Saved Forever</li>
                        </ul>
                    </template>
                    <template v-else-if="organization.subscription_status == 'graceperiod' && organization.subscriptions[0].ends_at">
                        <h2 class="mb-4 mt-n2">Account Canceled</h2>
                        <div>Your account has been canceled and will become inactive on {{ organization.subscriptions[0].ends_at | toDate }}.</div>
                    </template>
                    <template v-else-if="organization.subscription_status == 'graceperiod'">
                        <h2 class="mb-4 mt-n2">Account Canceled</h2>
                        <div>Your account has been canceled.</div>
                    </template>
                    <template v-else-if="organization.subscription_status == 'past_due'">
                        <h2 class="mb-4 mt-n2">Account Billing Issue</h2>
                        <div class="alert alert-danger">Your account has a payment issue and will be cancelled soon if no action is taken! Please update your payment card.</div>
                    </template>
                    <template v-else-if="organization.subscription_status === 'canceled'">
                        <h2 class="mb-4 mt-n2">Account Canceled</h2>
                        <div>Your account has been canceled.</div>
                    </template>
                    <template v-else-if="organization.subscription_status === 'active' && organization.plan_name">
                        <h2 class="mb-4 mt-n2">{{ nameofPlan }} Plan</h2>
                        <h3 class="card-title pricing-card-title">{{ getPlanCost }} <small class="text-muted"><template v-if="organization.period === 'monthly'">/ month</template><template v-else>/ year</template></small></h3>
                        <ul class="pricing-list list-unstyled my-3">
                            <li><strong>{{ getEmployees(organization.allowed_employees) }}</strong> Employees</li>
                            <li><strong>{{ getTemplates(organization.templates) }}</strong> Templated Signature Designs</li>
                            <li>All Premium Features</li>
                        </ul>
                        <div class="mt-4 flex">
                            <button type="button" class="btn btn-mediumblue" v-on:click="startTheUpgrade();">Modify Plan</button>
                        </div>
                        <div class="mt-2"><a href="/billing/cancel" class="btn btn-link btn-link-cancel px-1 py-0 text-xsmall">Cancel My Account</a></div>
                    </template>
                </div>
                <div class="card-body card-body-padding" v-else>
                    <div class="p-5 text-center">
                        <div class="inline-loading"></div>
                    </div>
                </div>
            </div>

            <div class="card billing-card mb-4" v-if="loaded && organization && organization.subscription_status !== 'canceled'">
                <div class="card-body card-body-padding">

                    <h2 class="mb-4 mt-n2">Credit Card</h2>

                    <div class="alert alert-success" v-if="success && hasOutStandingPayment && outStandingPaymentAmountPaid">
                        Your credit card has been updated and your outstanding invoice for ${{ outStandingPaymentAmountPaid / 100 }} has been paid.
                    </div>
                    <div class="alert alert-success" v-else-if="success">
                        Your credit card has been updated
                    </div>
                    <div class="existing-card d-flex align-items-center justify-content-between mb-2" v-if="card_last4">
                        <div>
                            <img v-bind:src="'/img/credit-cards/' + card_brand + '.png'" width="64" height="36" class="mr-2">
                            ... <span v-text="card_last4"></span>
                        </div>
                        <div>
                            Exp <span v-text="card_exp_month"></span>/<span v-text="card_exp_year"></span>
                        </div>
                    </div>
                    <form method="POST" v-if="!success">
                        <div class="form-group">
                            <label for="card-element" class="sr-only">Credit or Debit Card</label>
                            <div class="form-control">
                                <div ref="card"></div>
                            </div>
                            <span class="invalid-feedback" role="alert" v-if="error">
                                <strong v-text="error"></strong>
                            </span>
                        </div>
                        <div class="text-center mt-3">
                            <button class="btn btn-primary btn-block" v-on:click.prevent="update()" v-html="updateButtonText" v-bind:disabled="updateLoading"></button>
                            <a href="#" class="mt-3 remove-card" v-if="organization.plan_name == 'solo' && card_last4" v-on:click.prevent="removeCard()">Remove Card</a>
                        </div><!-- .text-center -->
                    </form>
                </div>
            </div><!-- .billing-card -->

            <div class="card billing-card">
                <div class="card-body card-body-padding" v-if="loaded">
                    <h2 class="mb-4 mt-n2">Invoices</h2>
                    <table class="table table-bottom-border mb-0">
                        <tr v-for="(invoice, index) in invoices" v-bind:key="index">
                            <td class="text-small align-middle pl-0">{{ invoice.date | toDate }}</td>
                            <td class="text-small align-middle">{{ invoice.total }}</td>
                            <td class="text-small align-middle text-right pr-0"><a v-bind:href="invoice.invoice_pdf" class="btn btn-mediumblue btn-sm" target="_blank">Download</a></td>
                        </tr>
                    </table>
                </div>
                <div class="card-body card-body-padding" v-else>
                    <div class="p-5 text-center">
                        <div class="inline-loading"></div>
                    </div>
                </div>
            </div><!-- .billing-card -->

        </div>

        <upgrade-modal v-if="loaded && user && organization" v-bind:show-upgrade-modal="showUpgradeModal" v-bind:user="user" v-bind:organization="organization" theme="light" v-on:upgradesuccessful="upgradeSuccessful" v-on:closeupgrademodal="showUpgradeModal = false"></upgrade-modal>

    </section>

</template>

<script>
    import { DateTime } from "luxon";
    export default {
        props: {
            load_stripe_key: {
                type: String,
                default: false,
            },
            // planName: {
            //     type: String,
            //     default: '',
            // }
        },
        data: function () {
            return {
                loaded: false,
                organization: null,
                user: null,
                plan: null,
                invoices: null,
                token: null,
                card_brand: null,
                card_last4: null,
                card_exp_month: null,
                card_exp_year: null,
                updateLoading: false,
                success:false,
                error: null,
                hasOutStandingPayment: false,
                outStandingPaymentAmountPaid: null,
                stripe: undefined,
                client_secret: null,
                stripeResult: null,
                elements: undefined,
                card: undefined,
                showUpgradeModal: false,
                style: {
                    base: {
                        color: '#333333',
                        fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
                        fontSmoothing: 'antialiased',
                        fontSize: '16px',
                        '::placeholder': {
                            color: '#999999'
                        }
                    },
                    invalid: {
                        color: '#fa755a',
                        iconColor: '#fa755a'
                    }
                },
            }
        },
        mounted() {
            var self = this;
            //console.log('Component mounted.')
            if (this.load_stripe_key) {
                axios.post('/billing/data', {})
                .then(function (response) {
                    if (response.data.client_secret) {
                        self.client_secret = response.data.client_secret;
                        self.organization = response.data.organization;
                        self.user = response.data.user;
                        self.plan = response.data.plan;
                        self.invoices = response.data.invoices;
                        self.card_brand = response.data.card_brand;
                        self.card_last4 = response.data.card_last4;
                        self.card_exp_month = response.data.card_exp_month;
                        self.card_exp_year = response.data.card_exp_year;
                        self.loaded = true;
                        self.$nextTick(function () {
                            self.stripe = Stripe(self.load_stripe_key);
                            self.elements = self.stripe.elements();
                            self.card = self.elements.create('card', {style: self.style});
                            self.card.mount(self.$refs.card);
                        });
                    }
                })
                .catch(function (error) {
                    console.log(error);
                });
            }
        },
        computed: {
            getPlanCost: function() {
                if (this.organization.plan_amount) {
                    var price = this.organization.plan_amount;
                    price = price.replace('.00', '');
                    return '$' + price;
                } else if (this.plan) {
                    if (this.organization.period === 'yearly') {
                        return '$' + this.plan.yearly_cost;
                    } else {
                        return '$' + this.plan.monthly_cost;
                    }
                }
                return '';
                //
            },
            nameofPlan: function() {
                if (this.organization.plan_name === 'enterprise') {
                    return "Enterprise";
                } else if (this.organization.plan_name === 'free') {
                    return "Complimentary";
                } else if (this.organization.plan_name === 'trial') {
                    return "Trial";
                } else {
                    return "Company";
                }
            },
            updateButtonText: function() {
                if (this.updateLoading) {
                    return "Please wait... <img src='/img/spinner-white.gif' width='30' height='30'>";
                } else if (this.card_last4 === null) {
                    return "Add Payment Card"
                } else {
                    return "Update Payment Card";
                }
            }
        },
        filters: {
            toDate: function(date) {
                if (date.indexOf("T") !== -1) {
                    return DateTime.fromISO(date).toLocaleString();
                } else {
                    return DateTime.fromSQL(date).toLocaleString();
                }
            },
            toLowerCase: function(string) {
                return string.toLowerCase().trim();
            }
        },
        methods: {
            startTheUpgrade: function() {
                this.showUpgradeModal = true;
            },
            upgradeSuccessful: function(plan_name, period, templates, allowed_employees, sharing, monthly_cost, yearly_cost) {
                this.organization.plan_name = plan_name;
                this.organization.period = period;
                this.organization.templates = templates;
                this.organization.allowed_employees = allowed_employees;
                this.organization.sharing = sharing;
                this.plan.monthly_cost = monthly_cost;
                this.plan.yearly_cost = yearly_cost;
            },
            getTemplates: function(templates) {
                if (templates === 99999) {
                    return "Unlimited";
                } else {
                    return templates;
                }
            },
            getEmployees: function(employees) {
                if (employees === 99999) {
                    return "Unlimited";
                } else {
                    return employees;
                }
            },
            update: function () {
                var self = this;
                self.updateLoading = true;
                self.stripe.handleCardSetup(self.client_secret, self.card).then(function(result) {
                    // Handle result.error or result.setupIntent
                    if (result.error) {
                        self.error = result.error.message;
                        self.updateLoading = false;
                    } else {
                        self.error = null;
                        self.stripeResult = result;
                        axios.post('/billing/update', {payment_method: self.stripeResult.setupIntent.payment_method})
                        .then(function (response) {
                            if (response.data.status == 'success') {
                                self.purchaseSuccessful = true;
                                self.purchased = true;
                                self.updateLoading = false;
                                self.success = true;
                                self.card_brand = response.data.card.card_brand;
                                self.card_last4 = response.data.card.card_last4;
                                self.card_exp_month = response.data.card.card_exp_month;
                                self.card_exp_year = response.data.card.card_exp_year;
                                self.hasOutStandingPayment = response.data.has_past_due_invoice;
                                self.outStandingPaymentAmountPaid = response.data.past_due_invoice_amount_paid;
                            }
                            //self.$emit('purchasesuccessful', response.data.user, response.data.organization);
                        })
                        .catch(function (error) {
                            self.updateLoading = false;
                            if (error.response.data.errors) {
                                self.purchaseErrors = error.response.data.errors;
                            } else {
                                console.log(error);
                            }
                        });
                    }
                });
            },
            removeCard: function () {
                var confirmation = confirm("Are you sure you want to remove your credit card?");
                if (confirmation == true) {
                    var self = this;
                    axios.post('/billing/remove-card')
                    .then(function (response) {
                        if (response.data.status == 'success') {
                            self.card_brand = null;
                            self.card_last4 = null;
                            self.card_exp_month = null;
                            self.card_exp_year = null;
                        }
                    })
                    .catch(function (error) {
                        console.log(error);
                    });
                }
            }
        }
    }
</script>
